import { useEffect, useRef } from 'react';

import { mutate } from 'swr';

import { FeedItem } from '@/lib/api';
import { API_URL } from '@/lib/consts';

interface UseFeedSummaryPollingOptions {
  feedItems: FeedItem[];
  feedEndpoint: string;
  enabled: boolean;
}

/**
 * Custom hook that polls for thread summaries on feed items that don't have them yet.
 * Uses exponential backoff to avoid overwhelming the server.
 */
export function useFeedSummaryPolling({
  feedItems,
  feedEndpoint,
  enabled,
}: UseFeedSummaryPollingOptions) {
  const timeoutsRef = useRef<Map<number, NodeJS.Timeout>>(new Map());
  const attemptsRef = useRef<Map<number, number>>(new Map());

  useEffect(() => {
    if (!enabled || !feedItems) {
      // Clear all timeouts if polling is disabled
      timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      timeoutsRef.current.clear();
      attemptsRef.current.clear();
      return;
    }

    // Find feed items without summaries
    const itemsWithoutSummaries = feedItems
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => !item.summary);

    // Set up polling for each item without a summary
    itemsWithoutSummaries.forEach(({ index }) => {
      // Skip if we're already polling this item
      if (timeoutsRef.current.has(index)) {
        return;
      }

      const attempts = attemptsRef.current.get(index) || 0;

      // Stop polling after 10 attempts (roughly 17 minutes total)
      if (attempts >= 10) {
        return;
      }

      // Exponential backoff: 2^attempts seconds, min 2s, max 300s (5 minutes)
      const baseDelay = Math.min(Math.max(2 ** attempts, 2), 300);
      const delay = baseDelay * 1000; // Convert to milliseconds

      const timeout = setTimeout(async () => {
        try {
          // Trigger a revalidation of the feed data
          await mutate(`${API_URL}/feed/${feedEndpoint}`);

          // Increment attempt count
          attemptsRef.current.set(index, attempts + 1);

          // Clear this timeout from our map
          timeoutsRef.current.delete(index);
        } catch (error) {
          console.warn('Failed to revalidate feed data:', error);
          // Still increment attempts to avoid infinite retries
          attemptsRef.current.set(index, attempts + 1);
          timeoutsRef.current.delete(index);
        }
      }, delay);

      // Store the timeout so we can cancel it if needed
      timeoutsRef.current.set(index, timeout);
    });

    // Clean up timeouts for items that now have summaries
    const itemsWithSummaries = new Set(
      feedItems
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => item.summary)
        .map(({ index }) => index)
    );

    timeoutsRef.current.forEach((timeout, index) => {
      if (itemsWithSummaries.has(index)) {
        clearTimeout(timeout);
        timeoutsRef.current.delete(index);
        attemptsRef.current.delete(index);
      }
    });

    // Cleanup function
    return () => {
      const timeouts = timeoutsRef.current;
      timeouts.forEach((timeout) => clearTimeout(timeout));
      timeouts.clear();
    };
  }, [feedItems, feedEndpoint, enabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const timeouts = timeoutsRef.current;
      const attempts = attemptsRef.current;
      timeouts.forEach((timeout) => clearTimeout(timeout));
      timeouts.clear();
      attempts.clear();
    };
  }, []);
}
